import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
    Search, 
    Mail, 
    Phone, 
    Building2, 
    Globe, 
    Calendar,
    Trash2,
    CheckCircle,
    Clock,
    XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
    new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    contacted: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    qualified: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    closed: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
};

export default function LeadsManagement() {
    const { getAuthHeader } = useAuth();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchLeads();
    }, []);

    const fetchLeads = async () => {
        try {
            const response = await axios.get(`${API}/leads`, getAuthHeader());
            setLeads(response.data);
        } catch (error) {
            console.error('Error fetching leads:', error);
            toast.error('Failed to fetch leads');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (leadId, status) => {
        try {
            await axios.patch(`${API}/leads/${leadId}?status=${status}`, {}, getAuthHeader());
            setLeads(prev => prev.map(lead => 
                lead.id === leadId ? { ...lead, status } : lead
            ));
            toast.success('Lead status updated');
        } catch (error) {
            console.error('Error updating lead:', error);
            toast.error('Failed to update lead');
        }
    };

    const deleteLead = async (leadId) => {
        if (!window.confirm('Are you sure you want to delete this lead?')) return;
        
        try {
            await axios.delete(`${API}/leads/${leadId}`, getAuthHeader());
            setLeads(prev => prev.filter(lead => lead.id !== leadId));
            toast.success('Lead deleted');
        } catch (error) {
            console.error('Error deleting lead:', error);
            toast.error('Failed to delete lead');
        }
    };

    const filteredLeads = leads.filter(lead => {
        const matchesSearch = 
            lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lead.company?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div data-testid="leads-management">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <h1 className="text-3xl font-bold">Leads</h1>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search leads..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 w-64"
                            data-testid="leads-search"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-10 px-3 rounded-lg border border-input bg-background text-sm"
                        data-testid="leads-status-filter"
                    >
                        <option value="all">All Status</option>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="p-6 rounded-2xl border border-border bg-background">
                            <div className="h-6 skeleton rounded w-1/3 mb-4" />
                            <div className="h-4 skeleton rounded w-1/2" />
                        </div>
                    ))}
                </div>
            ) : filteredLeads.length > 0 ? (
                <div className="space-y-4">
                    {filteredLeads.map((lead) => (
                        <div 
                            key={lead.id}
                            className="p-6 rounded-2xl border border-border bg-background"
                            data-testid={`lead-${lead.id}`}
                        >
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <h3 className="text-lg font-semibold">{lead.name}</h3>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[lead.status]}`}>
                                            {lead.status}
                                        </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                                        <a 
                                            href={`mailto:${lead.email}`}
                                            className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                                        >
                                            <Mail className="w-4 h-4" />
                                            {lead.email}
                                        </a>
                                        <a 
                                            href={`tel:${lead.phone}`}
                                            className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                                        >
                                            <Phone className="w-4 h-4" />
                                            {lead.phone}
                                        </a>
                                        {lead.company && (
                                            <span className="flex items-center gap-2 text-muted-foreground">
                                                <Building2 className="w-4 h-4" />
                                                {lead.company}
                                            </span>
                                        )}
                                        {lead.website && (
                                            <a 
                                                href={lead.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Website
                                            </a>
                                        )}
                                    </div>

                                    {lead.message && (
                                        <p className="mt-3 text-sm text-muted-foreground">
                                            {lead.message}
                                        </p>
                                    )}

                                    <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {formatDate(lead.created_at)}
                                        </span>
                                        {lead.source_page && (
                                            <span>Source: {lead.source_page}</span>
                                        )}
                                        {lead.service_interest && (
                                            <span>Interest: {lead.service_interest}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <select
                                        value={lead.status}
                                        onChange={(e) => updateStatus(lead.id, e.target.value)}
                                        className="h-9 px-3 rounded-lg border border-input bg-background text-sm"
                                    >
                                        <option value="new">New</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="qualified">Qualified</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteLead(lead.id)}
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-20">
                    <p className="text-lg text-muted-foreground mb-4">No leads found</p>
                    {searchQuery || statusFilter !== 'all' ? (
                        <Button 
                            variant="outline" 
                            onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                            className="rounded-full"
                        >
                            Clear Filters
                        </Button>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Leads from your website forms will appear here.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
